/**
 * nh-birthday-gift.js
 *
 * Keeps the $0 birthday gift candle (variant 43362186952781, SKU
 * NH-GIFT-CANDLE-BDAY) in sync with the birthday discount code on the cart.
 *
 * Rules, per the 2026 birthday flow spec:
 *   - A discount code starting with "NHBDAY-" is applied  → the gift candle
 *     is in the cart at quantity 1, and the cart attribute
 *     "Birthday gift: Fig Tree Candle" is set (backend text on the order).
 *   - The code is removed → the candle and the attribute are removed.
 *   - Never more than one candle (quantity is clamped back to 1).
 *   - Out of stock or any cart-API failure → fail silently. The Shopify Flow
 *     backstop tags the order `add-candle` so CS can add it at fulfillment.
 *
 * Codes carried by the cart but not applicable (applicable: false) do not
 * earn the gift — Shopify keeps rejected codes in the discount_codes array,
 * and a typo'd NHBDAY- code must not add a free product.
 *
 * Known limit (accepted): Shopify checkout is closed to theme code, so a
 * code typed at checkout never reaches this script. That is exactly what the
 * `add-candle` Flow backstop is for.
 *
 * Hook points (mirrors nh-cart-delivery-refresh.js):
 *   DOMContentLoaded    — cart page / drawer first render
 *   cart:update event   — any cart mutation (add, qty change, remove)
 *   discount:update     — code applied or removed via the cart discount field
 *   pageshow persisted  — bfcache restore after back-navigation
 *
 * Dependency-free on purpose (no @theme/* imports), like its sibling
 * nh-cart-delivery-refresh.js, so it cannot be broken by import-map changes.
 */

const GIFT_VARIANT_ID = 43362186952781;
const CODE_PREFIX = 'NHBDAY-';
const CART_ATTRIBUTE = 'Birthday gift';
const CART_ATTRIBUTE_VALUE = 'Fig Tree Candle';
const SOURCE_ID = 'nh-birthday-gift';

/**
 * Pure decision function — given a /cart.js payload, returns the actions
 * needed to reconcile the cart. Kept side-effect free so the logic is
 * provable with a harness (see CLAUDE.md verification rule).
 *
 * @param {Object} cart - The /cart.js response body.
 * @returns {{ addGift: boolean,
 *             removeKeys: string[],
 *             clampKey: string | null,
 *             setAttribute: string | null }}
 *   removeKeys — line-item keys to set to quantity 0
 *   clampKey   — line-item key to set back to quantity 1
 *   setAttribute — 'set' | 'clear' | null (null = leave attributes alone)
 */
export function decideGiftActions(cart) {
  const none = { addGift: false, removeKeys: [], clampKey: null, setAttribute: null };
  if (!cart || !Array.isArray(cart.items)) return none;

  const hasCode = (cart.discount_codes || []).some(function (entry) {
    return (
      entry &&
      typeof entry.code === 'string' &&
      entry.code.toUpperCase().startsWith(CODE_PREFIX) &&
      entry.applicable !== false
    );
  });

  const giftLines = cart.items.filter(function (item) {
    return item && item.variant_id === GIFT_VARIANT_ID;
  });
  const giftQuantity = giftLines.reduce(function (sum, item) {
    return sum + (item.quantity || 0);
  }, 0);

  const attributeValue = cart.attributes ? cart.attributes[CART_ATTRIBUTE] : undefined;

  if (hasCode) {
    const actions = { addGift: false, removeKeys: [], clampKey: null, setAttribute: null };
    if (giftQuantity === 0) {
      actions.addGift = true;
    } else if (giftQuantity > 1) {
      // Clamp the first gift line back to 1, remove any extra gift lines.
      actions.clampKey = giftLines[0].key || null;
      actions.removeKeys = giftLines.slice(1).map(function (line) {
        return line.key;
      }).filter(Boolean);
    }
    if (attributeValue !== CART_ATTRIBUTE_VALUE) actions.setAttribute = 'set';
    return actions;
  }

  // No (applicable) birthday code: the gift and its attribute must go.
  return {
    addGift: false,
    removeKeys: giftLines.map(function (line) { return line.key; }).filter(Boolean),
    clampKey: null,
    setAttribute: attributeValue ? 'clear' : null,
  };
}

// ── Cart API plumbing ────────────────────────────────────────────────────────

function routes() {
  const themeRoutes = (window.Theme && window.Theme.routes) || {};
  const cartUrl = themeRoutes.cart_url || '/cart';
  return {
    cart: cartUrl + '.js',
    add: themeRoutes.cart_add_url || '/cart/add.js',
    change: themeRoutes.cart_change_url || '/cart/change.js',
    update: themeRoutes.cart_update_url || '/cart/update.js',
  };
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error('Cart request failed: ' + response.status);
  return response.json();
}

async function fetchCart() {
  const response = await fetch(routes().cart, { credentials: 'same-origin' });
  if (!response.ok) throw new Error('Cart fetch failed: ' + response.status);
  return response.json();
}

/**
 * Notify the theme that the cart changed so cart items, summary and the
 * header bubble re-render. Shape mirrors CartUpdateEvent in events.js —
 * with no `data.sections`, component-cart-items.js falls back to
 * sectionRenderer.renderSection(..., { cache: false }), a full re-render.
 * @param {Object} cart - Fresh /cart.js payload.
 */
function notifyCartUpdate(cart) {
  const event = new Event('cart:update', { bubbles: true });
  /** @type {any} */ (event).detail = {
    resource: cart,
    sourceId: SOURCE_ID,
    data: { source: SOURCE_ID, itemCount: cart.item_count },
  };
  document.dispatchEvent(event);
}

// ── Reconciler ───────────────────────────────────────────────────────────────

let busy = false;
let rerunRequested = false;

async function reconcile() {
  if (busy) {
    rerunRequested = true;
    return;
  }
  busy = true;

  try {
    const cart = await fetchCart();
    const actions = decideGiftActions(cart);
    const urls = routes();
    let mutated = false;
    let addSucceeded = false;

    if (actions.addGift) {
      try {
        await postJson(urls.add, { items: [{ id: GIFT_VARIANT_ID, quantity: 1 }] });
        addSucceeded = true;
        mutated = true;
      } catch (error) {
        // Out of stock (422) or any add failure: fail silently rather than
        // breaking the cart. The add-candle Flow backstop covers the order.
      }
    }

    if (actions.clampKey) {
      await postJson(urls.change, { id: actions.clampKey, quantity: 1 });
      mutated = true;
    }

    for (const key of actions.removeKeys) {
      await postJson(urls.change, { id: key, quantity: 0 });
      mutated = true;
    }

    // Only stamp the attribute if the gift is (or already was) in the cart:
    // when the add itself failed, the attribute would promise a candle the
    // order does not contain.
    const giftInCart = actions.addGift ? addSucceeded : true;

    if (actions.setAttribute === 'set' && giftInCart) {
      const attributes = {};
      attributes[CART_ATTRIBUTE] = CART_ATTRIBUTE_VALUE;
      await postJson(urls.update, { attributes: attributes });
      mutated = true;
    } else if (actions.setAttribute === 'clear') {
      const attributes = {};
      attributes[CART_ATTRIBUTE] = '';
      await postJson(urls.update, { attributes: attributes });
      mutated = true;
    }

    if (mutated) {
      notifyCartUpdate(await fetchCart());
    }
  } catch (error) {
    // Never let gift logic break the cart page.
  } finally {
    busy = false;
    if (rerunRequested) {
      rerunRequested = false;
      reconcile();
    }
  }
}

// ── Hooks ────────────────────────────────────────────────────────────────────

function onCartEvent(event) {
  // Skip the echo of our own mutations — reconcile is idempotent, this just
  // saves a redundant /cart.js round trip.
  const detail = /** @type {any} */ (event).detail;
  if (detail && detail.sourceId === SOURCE_ID) return;
  reconcile();
}

// The typeof guard lets the harness import the pure decision function from a
// .mjs copy under Node, where there is no document (CLAUDE.md verification rule).
if (typeof document !== 'undefined') {
  document.addEventListener('cart:update', onCartEvent);
  document.addEventListener('discount:update', onCartEvent);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) reconcile();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      reconcile();
    });
  } else {
    reconcile();
  }
}
